import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Link,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {alpha} from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {Link as RouterLink, useLocation, useNavigate, useParams} from 'react-router';
import {getReportById, updateReportDoc} from '../../../firebase/firestore/reports';
import {useSession} from '../../../firebase/UserProvider';
import useResizablePanelWidth from '../../../hooks/useResizablePanelWidth';
import BasicRichTextEditor from '../components/BasicRichTextEditor';
import IirBodyRenderer from '../components/IirBodyRenderer';
import IirMaterialsPanel from '../components/IirMaterialsPanel';
import EntityTagsSection, {TAGS_SECTION_HEADING_SX} from '../../collection-requests/tags/EntityTagsSection';
import {
    collectionTaskPath,
    hasAnyRole,
    IIR_TAG_ROLES,
    sanitizeReturnTo,
} from '../collectionTaskPayload';
import {
    approveCollectionIir,
    getCollectionIir,
    getCollectionTask,
    getIirLatestBody,
    modifyCollectionIir,
    persistReportPmJson,
    submitCollectionIir,
} from '../collectionTaskApi';
import {isSubstantivePmJson} from '../pmJsonContent';
import openCollectionMaterial from '../viewer/openCollectionMaterial';
import openCollectionIir from '../viewer/openCollectionIir';

const TITLE_SAVE_DEBOUNCE_MS = 600;

const PANEL_WIDTH = 360;
const PANEL_MIN_WIDTH = 260;
const PANEL_MAX_WIDTH = 760;
const COLLAPSED_STRIP_WIDTH = 28;
const TOGGLE_SIZE = 24;

function locationFromReturnTo(returnTo, fallback = '/collection/iir') {
    const safe = sanitizeReturnTo(returnTo) || fallback;
    const [pathPart, queryPart = ''] = safe.split('?');
    const [pathname, hash = ''] = pathPart.split('#');
    return {
        pathname: pathname || fallback,
        search: queryPart ? `?${queryPart.split('#')[0]}` : '',
        hash: hash ? `#${hash}` : '',
    };
}

export default function IirEditorPage() {
    const {reportId} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const {user, role, roles} = useSession() || {};
    const [report, setReport] = useState(null);
    const [iir, setIir] = useState(null);
    const [ctNumber, setCtNumber] = useState(null);
    const [taskTitle, setTaskTitle] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');
    const titleSaveTimeoutRef = useRef(null);
    const redirectedRef = useRef(false);
    const editorViewRef = useRef(null);
    const submittedBodyRef = useRef(null);
    const [submittedBody, setSubmittedBody] = useState(null);
    const {width: panelWidth, isResizing, onResizeStart} = useResizablePanelWidth({
        storageKey: 'iirEditor.leftPanelWidth',
        defaultWidth: PANEL_WIDTH,
        min: PANEL_MIN_WIDTH,
        max: PANEL_MAX_WIDTH,
    });

    useEffect(() => () => {
        if (titleSaveTimeoutRef.current) {
            clearTimeout(titleSaveTimeoutRef.current);
        }
    }, []);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [r, summary] = await Promise.all([
                getReportById(reportId),
                getCollectionIir(reportId).catch(() => null),
            ]);
            if (!r) {
                setReport(null);
                setIir(null);
                setError('Not found');
                return;
            }
            setReport(r);
            setTitleDraft(r.title || '');
            setIir(summary);
            const taskId = summary?.collectionTaskId || r.meta?.collectionTaskId;
            if (taskId) {
                // The source task carries the CT number/title when the summary lacks them.
                try {
                    const task = await getCollectionTask(taskId);
                    setCtNumber(summary?.ctNumber || task?.ctNumber || null);
                    setTaskTitle(summary?.taskTitle || task?.title || null);
                } catch {
                    setCtNumber(summary?.ctNumber || null);
                    setTaskTitle(summary?.taskTitle || null);
                }
            } else {
                setCtNumber(summary?.ctNumber || null);
                setTaskTitle(summary?.taskTitle || null);
            }
        } catch (e) {
            setError(e?.message || 'Failed to load IIR');
            setReport(null);
            setIir(null);
        } finally {
            setLoading(false);
        }
    }, [reportId]);

    useEffect(() => {
        reload();
    }, [reload]);

    // View-mode is the default for non-draft / non-author opens. Stay on the editor
    // only for: admin, author of a draft, author reopening via Modify (canModify),
    // or a collection manager approving a submitted IIR (canApprove).
    useEffect(() => {
        if (loading || !iir || redirectedRef.current) return;
        const isAdmin = hasAnyRole(role, roles, ['admin']);
        if (isAdmin) return;
        if (iir.canApprove) return;
        const ownerId = iir.ownerUserId || null;
        const isAuthor = Boolean(user?.uid && ownerId && user.uid === ownerId);
        const isDraft = String(iir.workflowStatus || iir.status || '').toLowerCase() === 'draft';
        if (isAuthor && (isDraft || iir.canModify)) return;
        redirectedRef.current = true;
        const returnTo = sanitizeReturnTo(location.state?.returnTo) || '/collection/iir';
        openCollectionMaterial({
            materialId: reportId,
            materialKind: 'iir',
            returnTo,
            navigate,
            location: locationFromReturnTo(returnTo),
        });
    }, [loading, iir, role, roles, user?.uid, reportId, navigate, location.state?.returnTo]);

    const handleBack = () => {
        const returnTo = sanitizeReturnTo(location.state?.returnTo);
        if (returnTo) {
            navigate(returnTo);
            return;
        }
        navigate(-1);
    };

    const handleSubmit = async () => {
        setBusy(true);
        setError(null);
        try {
            // Stage in-memory editor content before submit. ws-collaboration debounces
            // snapshot persistence (~10s), so the latest report_versions row may still
            // be empty when we switch to the read-only NER renderer after submit.
            const pmJson = editorViewRef.current?.state?.doc?.toJSON?.();
            if (pmJson && pmJson.type === 'doc') {
                submittedBodyRef.current = pmJson;
                setSubmittedBody(pmJson);
                if (isSubstantivePmJson(pmJson)) {
                    try {
                        await persistReportPmJson(reportId, pmJson);
                    } catch (persistErr) {
                        console.warn('[iir-submit] snapshot persist failed (continuing):', persistErr);
                    }
                }
            }
            const summary = await submitCollectionIir(reportId);
            setIir(summary);
            setReport((prev) => (prev ? {
                ...prev,
                status: summary.status || 'submitted',
                meta: {
                    ...(prev.meta || {}),
                    workflowStatus: summary.workflowStatus || 'submitted',
                },
            } : prev));
        } catch (e) {
            setError(e?.message || 'Failed to submit IIR');
        } finally {
            setBusy(false);
        }
    };

    const handleApprove = async () => {
        setBusy(true);
        setError(null);
        try {
            const summary = await approveCollectionIir(reportId);
            const returnTo = sanitizeReturnTo(location.state?.returnTo) || '/collection/iir';
            openCollectionIir({
                iir: summary,
                userId: user?.uid,
                returnTo,
                navigate,
                location: locationFromReturnTo(returnTo),
            });
        } catch (e) {
            setError(e?.message || 'Failed to approve IIR');
        } finally {
            setBusy(false);
        }
    };

    const handleModify = async () => {
        setBusy(true);
        setError(null);
        try {
            let seed = submittedBodyRef.current || submittedBody;
            if (!isSubstantivePmJson(seed)) {
                try {
                    const body = await getIirLatestBody(reportId);
                    if (body?.pmJson && isSubstantivePmJson(body.pmJson)) {
                        seed = body.pmJson;
                        submittedBodyRef.current = seed;
                        setSubmittedBody(seed);
                    }
                } catch {
                    // best-effort — editor will still mount with any in-memory seed
                }
            }
            const summary = await modifyCollectionIir(reportId);
            setIir(summary);
            setReport((prev) => (prev ? {
                ...prev,
                status: summary.status || 'draft',
                meta: {
                    ...(prev.meta || {}),
                    workflowStatus: summary.workflowStatus || 'draft',
                },
            } : prev));
        } catch (e) {
            setError(e?.message || 'Failed to reopen IIR');
        } finally {
            setBusy(false);
        }
    };

    const persistTitle = useCallback(async (nextTitle) => {
        const cleaned = String(nextTitle || '').trim();
        if (!cleaned || cleaned === (report?.title || '').trim()) {
            return;
        }
        try {
            await updateReportDoc(reportId, {title: cleaned});
            setReport((prev) => (prev ? {...prev, title: cleaned} : prev));
            setIir((prev) => (prev ? {...prev, title: cleaned} : prev));
        } catch (e) {
            setError(e?.message || 'Failed to update title');
        }
    }, [report?.title, reportId]);

    const handleTitleChange = (event) => {
        const next = event.target.value;
        setTitleDraft(next);
        if (titleSaveTimeoutRef.current) {
            clearTimeout(titleSaveTimeoutRef.current);
        }
        titleSaveTimeoutRef.current = setTimeout(() => {
            persistTitle(next);
        }, TITLE_SAVE_DEBOUNCE_MS);
    };

    const handleTitleBlur = () => {
        if (titleSaveTimeoutRef.current) {
            clearTimeout(titleSaveTimeoutRef.current);
            titleSaveTimeoutRef.current = null;
        }
        if (!String(titleDraft || '').trim()) {
            setTitleDraft(report?.title || '');
            return;
        }
        persistTitle(titleDraft);
    };

    if (loading) {
        return <Stack alignItems="center" py={6}><CircularProgress/></Stack>;
    }
    if (error && !report) {
        return <Box p={2}><Alert severity="error">{error}</Alert></Box>;
    }
    if (!report) {
        return <Box p={2}><Alert severity="error">Not found</Alert></Box>;
    }

    const meta = report.meta || {};
    const taskId = iir?.collectionTaskId || meta.collectionTaskId;
    const workflowStatus = iir?.workflowStatus || meta.workflowStatus || report.status || 'draft';
    const isApproved = workflowStatus === 'approved';
    const isSubmitted = workflowStatus === 'submitted';
    const readOnly = iir ? !iir.editable : false;
    const canSubmit = Boolean(iir?.canSubmit);
    const canApprove = Boolean(iir?.canApprove);
    const canModify = Boolean(iir?.canModify);
    // Tags are metadata, not content — editable by collection roles regardless of
    // the draft/submitted/approved workflow status (P10, mirrors P9's CM decision).
    const canEditTags = hasAnyRole(role, roles, IIR_TAG_ROLES);
    const taskPath = collectionTaskPath(ctNumber || taskId);
    const taskLinkLabel = [ctNumber || taskId, taskTitle].filter(Boolean).join(' · ');

    const statusChip = (() => {
        if (isApproved) {
            return {label: 'Approved', color: 'success', variant: 'filled'};
        }
        if (isSubmitted) {
            return {label: 'Submitted', color: 'info', variant: 'filled'};
        }
        return {label: 'Draft', color: 'default', variant: 'outlined'};
    })();

    const header = (
        <Box px={2} py={1} borderBottom="1px solid #CECFD2">
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={1}>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleBack}
                    sx={{minWidth: 'auto', whiteSpace: 'nowrap'}}
                >
                    Back
                </Button>
                <Stack direction="row" spacing={1} flexShrink={0} sx={{whiteSpace: 'nowrap'}}>
                    {canSubmit && (
                        <Button
                            variant="contained"
                            color="success"
                            disabled={busy}
                            sx={{whiteSpace: 'nowrap'}}
                            onClick={handleSubmit}
                        >
                            Submit
                        </Button>
                    )}
                    {canApprove && (
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={busy}
                            sx={{whiteSpace: 'nowrap'}}
                            onClick={handleApprove}
                        >
                            Approve
                        </Button>
                    )}
                    {canModify && (
                        <Button
                            variant="outlined"
                            disabled={busy}
                            sx={{whiteSpace: 'nowrap'}}
                            onClick={handleModify}
                        >
                            Modify
                        </Button>
                    )}
                </Stack>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap mb={0.5}>
                <Typography variant="caption" color="text.secondary">
                    IIR
                    {iir?.iirNumber ? ` · ${iir.iirNumber}` : ''}
                </Typography>
                <Chip
                    size="small"
                    label={statusChip.label}
                    color={statusChip.color}
                    variant={statusChip.variant}
                />
            </Stack>

            {/* Full-width title — not constrained by the action buttons row. */}
            {readOnly ? (
                <Typography variant="h6" sx={{width: '100%', lineHeight: 1.4}}>
                    {report.title}
                </Typography>
            ) : (
                <TextField
                    value={titleDraft}
                    onChange={handleTitleChange}
                    onBlur={handleTitleBlur}
                    placeholder="IIR title"
                    variant="standard"
                    fullWidth
                    multiline
                    InputProps={{disableUnderline: true}}
                    inputProps={{
                        'aria-label': 'IIR title',
                        maxLength: 200,
                    }}
                    sx={{
                        width: '100%',
                        mt: 0.25,
                        '& .MuiInputBase-root': {width: '100%'},
                        '& .MuiInputBase-input': {
                            fontSize: '1.25rem',
                            fontWeight: 500,
                            lineHeight: 1.4,
                            padding: 0,
                        },
                    }}
                />
            )}

            {taskLinkLabel && taskPath && (
                <Box mt={0.75} data-testid="iir-parent-task">
                    <Typography sx={TAGS_SECTION_HEADING_SX}>PARENT TASK</Typography>
                    <Link
                        component={RouterLink}
                        to={taskPath}
                        underline="hover"
                        sx={{fontFamily: 'monospace', fontSize: '0.875rem'}}
                    >
                        {taskLinkLabel}
                    </Link>
                </Box>
            )}

            {error && <Alert severity="error" sx={{mt: 1}}>{error}</Alert>}
            <Box py={1}>
                <EntityTagsSection
                    entityType="REPORT"
                    entityId={reportId}
                    canEdit={canEditTags}
                    enabled={Boolean(reportId)}
                    dense
                />
            </Box>
        </Box>
    );

    return (
        <Stack
            direction="row"
            height="100%"
            flexGrow={1}
            sx={{overflow: 'hidden'}}
        >
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    flexShrink: 0,
                    overflow: 'visible',
                    width: collapsed ? COLLAPSED_STRIP_WIDTH : panelWidth,
                    transition: isResizing ? 'none' : 'width 0.2s ease',
                }}
            >
                <Stack
                    sx={{
                        width: collapsed ? 0 : panelWidth,
                        overflow: 'hidden',
                        transition: isResizing ? 'none' : 'width 0.2s ease',
                        flexShrink: 0,
                        height: '100%',
                    }}
                >
                    <IirMaterialsPanel taskId={taskId}/>
                </Stack>

                {!collapsed && (
                    <Box
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="Resize left panel"
                        onPointerDown={onResizeStart}
                        sx={(theme) => ({
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            right: -3,
                            width: 6,
                            zIndex: 15,
                            cursor: 'col-resize',
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.25),
                            },
                            ...(isResizing && {
                                bgcolor: alpha(theme.palette.primary.main, 0.4),
                            }),
                        })}
                    />
                )}

                <Tooltip title={collapsed ? 'Expand panel' : 'Collapse panel'} placement="right" arrow>
                    <IconButton
                        size="small"
                        onClick={() => setCollapsed((c) => !c)}
                        aria-label={collapsed ? 'Expand panel' : 'Collapse panel'}
                        sx={(theme) => ({
                            position: 'absolute',
                            top: 52,
                            zIndex: 20,
                            width: TOGGLE_SIZE,
                            height: TOGGLE_SIZE,
                            bgcolor: 'background.paper',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '50%',
                            boxShadow: theme.shadows[2],
                            ...(collapsed
                                ? {
                                    left: (COLLAPSED_STRIP_WIDTH - TOGGLE_SIZE) / 2,
                                    right: 'auto',
                                }
                                : {
                                    left: 'auto',
                                    right: -TOGGLE_SIZE / 2,
                                }),
                            '&:hover': {bgcolor: 'action.hover'},
                            '& .MuiSvgIcon-root': {fontSize: '1rem'},
                        })}
                    >
                        {collapsed ? <ChevronRightIcon/> : <ChevronLeftIcon/>}
                    </IconButton>
                </Tooltip>
            </Box>

            <Divider orientation="vertical"/>

            <Box sx={{flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column'}}>
                {header}
                {readOnly ? (
                    // Submitted/approved IIRs render the persisted snapshot with NER/EL entity
                    // mentions (blue linked / yellow unlinked, hover cards) — the basic editor
                    // cannot display wikidataEntity marks (P11 / MINSKY-16308).
                        <IirBodyRenderer
                            key={`${reportId}:${workflowStatus}:ner`}
                            reportId={reportId}
                            stagedPmJson={submittedBodyRef.current || submittedBody}
                            sx={{flex: 1, minHeight: 0}}
                        />
                ) : (
                    // The editor sizes itself with height:100%; give it a flexed box to
                    // resolve against now that the header shares the column.
                    <Box sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}}>
                        <BasicRichTextEditor
                            key={`${reportId}:${workflowStatus}`}
                            documentId={reportId}
                            showDocumentHeader={false}
                            readOnly={readOnly}
                            seedPmJson={submittedBodyRef.current || submittedBody}
                            onReady={(view) => {
                                editorViewRef.current = view;
                            }}
                        />
                    </Box>
                )}
            </Box>
        </Stack>
    );
}
